import { useEffect, useState } from 'react'
import { format, isAfter, isBefore, isValid, parseISO, startOfDay } from 'date-fns'
import { DatePicker } from '@/components/ui/DatePicker'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import modalStyles from '@/components/ui/claimsModals.module.css'
import applicationModalStyles from '@/features/application/styles/modals.module.css'
import { saveResidentDefault } from '../../services/residentApi'
import type { ResidentDefaultFormValues, ResidentDetailResponse } from '../../types/resident'
import { RequiredAsterisk } from '../RequiredAsterisk'
import { ResidentDialogShell } from './ResidentDialogShell'

function parseYmd(value: string): Date | null {
  if (!value?.trim()) return null
  const iso = value.includes('T') ? value : `${value.slice(0, 10)}T12:00:00`
  const d = parseISO(iso)
  return isValid(d) ? d : null
}

function formatDisplayDate(value: unknown): string {
  const d = parseYmd(String(value ?? ''))
  return d ? format(d, 'MM/dd/yyyy') : ''
}

function toSaveYmd(value: string): string {
  const d = parseYmd(value)
  return d ? format(d, 'yyyy-MM-dd') : value
}

export function ReimbursementEndDateOverrideDialog({
  isOpen,
  onClose,
  userId,
  applicationId,
  formValues,
  systemCalculatedEndDate,
  currentEndDate,
  contractEndDate,
  existingReason,
  currentUserDisplayName,
  onSaved,
}: {
  isOpen: boolean
  onClose: () => void
  userId: string | number
  applicationId?: string | number
  formValues: ResidentDefaultFormValues
  systemCalculatedEndDate: string
  currentEndDate: string
  contractEndDate?: string
  existingReason?: string
  currentUserDisplayName: string
  onSaved: (
    savedDate: string,
    reason: string,
    response?: ResidentDetailResponse,
  ) => void | Promise<void>
}) {
  const [overrideEndDate, setOverrideEndDate] = useState('')
  const [overrideReason, setOverrideReason] = useState('')
  const [endDateError, setEndDateError] = useState('')
  const [reasonError, setReasonError] = useState('')
  const [generalError, setGeneralError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setOverrideEndDate(currentEndDate || systemCalculatedEndDate || '')
    setOverrideReason(existingReason || '')
    setEndDateError('')
    setReasonError('')
    setGeneralError('')
  }, [isOpen, currentEndDate, systemCalculatedEndDate, existingReason])

  const validate = (): boolean => {
    setEndDateError('')
    setReasonError('')
    setGeneralError('')

    let valid = true

    if (!overrideReason.trim()) {
      setReasonError('Override Reason is required.')
      valid = false
    }

    const overrideDate = parseYmd(overrideEndDate)
    if (!overrideDate) {
      setEndDateError('Override Reimbursement End Date is invalid.')
      valid = false
    } else {
      let dateError = ''

      if (formValues.first_month_not_paid_month && formValues.first_month_not_paid_year) {
        const firstMonth = parseYmd(
          `${formValues.first_month_not_paid_year}-${String(formValues.first_month_not_paid_month).padStart(2, '0')}-01`,
        )
        if (firstMonth && isBefore(startOfDay(overrideDate), startOfDay(firstMonth))) {
          dateError = 'Override End Date cannot be before First Month Rent Not Paid.'
        }
      }

      if (!dateError) {
        const startValue = formValues.adjusted_notice || formValues.rental_status_date
        const startDate = parseYmd(startValue)
        if (startDate && isBefore(startOfDay(overrideDate), startOfDay(startDate))) {
          dateError = 'Override End Date cannot be before Reimbursement Start Date.'
        }
      }

      if (!dateError) {
        // Angular validateReimbursementEndDateOverrideDates(): block dates after contract_end_date.
        const contractEnd = parseYmd(contractEndDate || '')
        if (contractEnd && isAfter(startOfDay(overrideDate), startOfDay(contractEnd))) {
          dateError = 'Override End Date cannot be after Contract End Date.'
        }
      }

      if (dateError) {
        setEndDateError(dateError)
        valid = false
      }
    }

    return valid
  }

  const handleSave = async () => {
    if (!validate()) return

    const previousDate = currentEndDate
    const savedDate = toSaveYmd(overrideEndDate)
    const payload: Record<string, unknown> = {
      page: 'resident',
      application_id: applicationId ?? null,
      reimbursement_end_date_override: '1',
      reimbursement_end_date_override_source: 'Manual Override',
      reimbursement_end_date_override_reason: overrideReason.trim(),
      adjust_max_movein_date: savedDate,
      adjusted_notice: formValues.adjusted_notice ? toSaveYmd(formValues.adjusted_notice) : '',
      rental_status_date: formValues.rental_status_date ? toSaveYmd(formValues.rental_status_date) : '',
      new_resident_lease_start_date: formValues.new_resident_lease_start_date
        ? toSaveYmd(formValues.new_resident_lease_start_date)
        : '',
    }
    if (formValues.first_month_not_paid_month && formValues.first_month_not_paid_year) {
      payload.first_month_not_paid = `${formValues.first_month_not_paid_year}-${String(formValues.first_month_not_paid_month).padStart(2, '0')}-01`
    }

    setSaving(true)
    try {
      const response = await saveResidentDefault(userId, payload)
      await onSaved(savedDate, overrideReason.trim(), response)
      onClose()
      showSuccess(
        `Reimbursement End Date updated from ${previousDate ? formatDisplayDate(previousDate) : 'blank'} to ${formatDisplayDate(savedDate)}.`,
      )
    } catch (err) {
      const message =
        (err as { response?: { data?: string } })?.response?.data ||
        'Unable to save Reimbursement End Date override.'
      setGeneralError(typeof message === 'string' ? message : 'Unable to save Reimbursement End Date override.')
      showApiError(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <ResidentDialogShell
      isOpen={isOpen}
      title="Override Reimbursement End Date"
      onClose={onClose}
      maxWidth="max-w-2xl"
      bodyScrollOnly
      panelDataTestId="reimbursement-end-date-override-dialog"
      overlayDataTestId="reimbursement-end-date-override-overlay"
      footer={
        <div
          className={modalStyles.modalFooterActionGroup}
          data-testid="reimbursement-end-date-override-footer"
        >
          <button
            type="button"
            className={modalStyles.modalActionPrimary}
            onClick={() => void handleSave()}
            disabled={saving}
            data-testid="reimbursement-end-date-override-save"
          >
            {saving ? 'Saving...' : 'Save Override'}
          </button>
          <button
            type="button"
            className={modalStyles.modalActionSecondary}
            onClick={onClose}
            disabled={saving}
            data-testid="reimbursement-end-date-override-cancel"
          >
            Cancel
          </button>
        </div>
      }
    >
      <div className="space-y-4" data-testid="reimbursement-end-date-override-body">
        <p className="text-xs leading-relaxed text-slate-500">
          Manual override for exceptional reimbursement scenarios.
        </p>

        <div className="rounded-[10px] border border-[#f4e5a1] bg-[#fffbed] px-3.5 py-3 text-xs leading-relaxed text-[#7a5a00]">
          You are overriding the system-calculated reimbursement end date. This action may impact
          reimbursement calculations, statements, and reports.
        </div>

        {generalError ? (
          <div
            className="rounded-lg border border-[#ffb8b8] bg-[#fff1f1] px-3 py-2.5 text-xs font-medium text-[#b42318]"
            data-testid="reimbursement-end-date-override-general-error"
          >
            {generalError}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-[#31527a]">
              System Calculated End Date
            </label>
            <input
              type="text"
              readOnly
              value={formatDisplayDate(systemCalculatedEndDate)}
              className="h-10 w-full cursor-not-allowed rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-[#232946]"
              data-testid="reimbursement-end-date-override-system-date"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-[#31527a]">
              Override End Date <RequiredAsterisk />
            </label>
            <DatePicker
              value={overrideEndDate}
              onChange={(v) => {
                setOverrideEndDate(v)
                setEndDateError('')
                setGeneralError('')
              }}
              allowFutureDates
              showHelperText={false}
              popperFixed
              error={endDateError || undefined}
              className="h-10 w-full rounded-md border border-[#e2e8f0] bg-white px-3 text-sm outline-none focus:border-[#1597ff] focus:ring-1 focus:ring-[#1597ff]"
              dataTestId="reimbursement-end-date-override-end-date"
            />
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-[#31527a]">
            Override Reason <RequiredAsterisk />
          </label>
          <textarea
            rows={4}
            maxLength={250}
            value={overrideReason}
            onChange={(e) => {
              setOverrideReason(e.target.value)
              setReasonError('')
              setGeneralError('')
            }}
            placeholder="Enter reason for manual override"
            aria-invalid={Boolean(reasonError)}
            className={`min-h-[110px] w-full resize-y rounded-md border bg-white px-3 py-2.5 text-sm outline-none focus:border-[#1597ff] focus:ring-1 focus:ring-[#1597ff] ${
              reasonError ? 'border-[#d01818]' : 'border-[#e2e8f0]'
            }`}
            data-testid="reimbursement-end-date-override-reason"
          />
          {reasonError ? (
            <p
              className={applicationModalStyles.formFieldError}
              role="alert"
              data-testid="reimbursement-end-date-override-reason-error"
            >
              {reasonError}
            </p>
          ) : null}
          <p className="mt-1.5 text-right text-[11px] text-[#9aa5b7]">{overrideReason.length} / 250</p>
        </div>

        <div className="grid grid-cols-1 gap-2.5 border-t border-[#edf1f7] pt-4 sm:grid-cols-3">
          <div className="min-w-0 rounded-lg border border-[#e5ebf3] bg-slate-50 px-3.5 py-3">
            <span className="mb-1 block text-[11px] text-[#8995a8]">Override Source</span>
            <strong className="block text-[13px] font-semibold leading-snug text-[#232946]">
              Manual Override
            </strong>
          </div>
          <div className="min-w-0 rounded-lg border border-[#e5ebf3] bg-slate-50 px-3.5 py-3">
            <span className="mb-1 block text-[11px] text-[#8995a8]">Updated By</span>
            <strong className="block break-words text-[13px] font-semibold leading-snug text-[#232946]">
              {currentUserDisplayName}
            </strong>
          </div>
          <div className="min-w-0 rounded-lg border border-[#e5ebf3] bg-slate-50 px-3.5 py-3">
            <span className="mb-1 block text-[11px] text-[#8995a8]">Policy</span>
            <strong className="block text-[13px] font-semibold leading-snug text-[#232946]">
              10-Day Return To Market
            </strong>
          </div>
        </div>
      </div>
    </ResidentDialogShell>
  )
}
